// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { PARENT_WIDTH, GROUP_NODE_HEIGHT } from '../constants/constants';

/* -------------------------------------------------------
   Fit view to nodes
------------------------------------------------------- */

// Returns the root-level visible nodes that should be used for viewport fitting.
// Excludes: hidden nodes, facility wrapper nodes, child nodes (relative positions).
const getFitNodes = nodes =>
  nodes.filter(n => !n.hidden && !n.data?.isHidden && n.type !== 'facility' && !n.parentId);

// Pure function: given the root nodes and viewport dimensions, returns the
// {x, y, zoom} that fits all nodes into view with the given padding.
// Uses node.position (absolute for root nodes) and node.data.width/height.
// Returns null when there are no root nodes to fit.
export const computeFitViewport = (
  nodes,
  viewW,
  viewH,
  { padding = 0.1, minZoom = 0.05, maxZoom = 2 } = {}
) => {
  const rootNodes = getFitNodes(nodes);
  if (!rootNodes.length) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of rootNodes) {
    const { x, y } = node.position;
    const w = node.data?.width ?? PARENT_WIDTH;
    const h = node.data?.height ?? GROUP_NODE_HEIGHT;
    if (x < minX) {
      minX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (x + w > maxX) {
      maxX = x + w;
    }
    if (y + h > maxY) {
      maxY = y + h;
    }
  }

  const boundsW = maxX - minX;
  const boundsH = maxY - minY;
  const scale = 1 + 2 * padding;

  if (boundsW === 0 || boundsH === 0) {
    return null;
  }

  const zoom = Math.min(
    Math.max(Math.min(viewW / (boundsW * scale), viewH / (boundsH * scale)), minZoom),
    maxZoom
  );

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  return {
    x: viewW / 2 - cx * zoom,
    y: viewH / 2 - cy * zoom,
    zoom,
  };
};

// Extract the trailing integer from a string.
// e.g. "Camera 1" -> 1, "Camera123" -> 123, "Camera" -> null
// (used for matching connection + rename to preserve number)
export const trailingInt = str => {
  const m = str?.match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
};

/* ============================================================
   ================= EQUIPMENT PROPERTIES/SETTING MAPPING  =====
   ============================================================ */
export const updateConfigObject = (
  stateObj = {},
  payloadObj = {},
  path = '',
  context = 'properties' // "properties" or "settings"
) => {
  Object.keys(payloadObj).forEach(key => {
    const payloadValue = payloadObj[key];
    const currentPath = path ? `${path}.${key}` : key;

    if (!(key in stateObj)) {
      return;
    }

    const stateValue = stateObj[key];

    // Recurse into nested objects
    if (typeof payloadValue === 'object' && payloadValue !== null && !Array.isArray(payloadValue)) {
      updateConfigObject(stateValue, payloadValue, currentPath, context);
      return;
    }

    // Detect existing value key
    const valueKey =
      'equip_setting_val_uuid' in stateValue
        ? 'equip_setting_val_uuid'
        : 'eprop_val_uuid' in stateValue
          ? 'eprop_val_uuid'
          : null;

    if (!valueKey) {
      // Create the appropriate key based on context propertie or ssetting
      if (context === 'settings') {
        stateValue.equip_setting_val_uuid = { value: payloadValue };
      } else {
        stateValue.eprop_val_uuid = { value: payloadValue };
      }
      return;
    }

    // Update existing value container
    const valueContainer = stateValue[valueKey];
    stateValue[valueKey] = {
      ...valueContainer,
      value: payloadValue,
    };
  });
};

export const buildEquipmentTree = (obj, mode = 'property') => {
  if (!obj || typeof obj !== 'object') {
    return [];
  }

  const isProperty = mode === 'property';

  const TYPE_KEY = isProperty ? 'property_type' : 'setting_type';
  const UUID_KEY = isProperty ? 'eprop_uuid' : 'equip_setting_uuid';
  const NAME_KEY = isProperty ? 'property_name' : 'setting_name';
  const LABEL_KEY = isProperty ? 'property_display_name' : 'setting_display_name';
  const VALUE_KEY = isProperty ? 'eprop_val_uuid' : 'equip_setting_val_uuid';
  const CHOICES_KEY = isProperty ? 'property_choices' : 'setting_choices';

  // Setting name to exclude from equipment details panel view
  const SETTINGS_TO_EXCLUDE = ['move_time', 'move_distance', 'pending_connection'];

  return Object.values(obj)
    .filter(
      item =>
        item &&
        typeof item === 'object' &&
        item[TYPE_KEY] &&
        !SETTINGS_TO_EXCLUDE.includes(item[NAME_KEY])
    )
    .map(item => {
      // CONTAINER
      if (item[TYPE_KEY] === 'Container') {
        const children = Object.values(item).filter(
          child => child && typeof child === 'object' && child[TYPE_KEY]
        );

        const builtChildren = buildEquipmentTree(children, mode);

        return {
          type: 'container',
          id: item[UUID_KEY],
          name: item[NAME_KEY],
          label: item[LABEL_KEY],
          children: builtChildren,
        };
      }

      // FIELD
      return {
        type: 'field',
        id: item[UUID_KEY],
        name: item[NAME_KEY],
        label: item[LABEL_KEY],
        value: item[VALUE_KEY]?.value,
        propertyType: item[TYPE_KEY],
        options:
          item[TYPE_KEY] === 'Choice'
            ? item[CHOICES_KEY]?.map(choice => ({
                value: choice.value,
                label: choice.display_value,
              }))
            : undefined,
      };
    })
    .filter(Boolean);
};

/* ================= RETURN FORM TYPE FROM VALUE TYPE ================== */

export const mapValueTypeToFormType = valueType => {
  const mapping = {
    Boolean: 'Checkbox',
    Integer: 'Number',
    Float: 'Number',
    String: 'Input',
    UUID: 'Input',
    'IP Address': 'Input',
    Choice: 'Select',
    Container: 'Container',
  };

  return mapping[valueType] || 'Input';
};

/* ---------- helper to get color status ---------- */
// TODO :  move to utils file in web-utils (only ok don t match variant name with badge variant)
export const getColorStatus = status => {
  switch (status) {
    case 'warning':
      return 'warning';
    case 'ok':
      return 'success';
    case 'error':
      return 'error';
    default:
      return 'mostudio';
  }
};

export const getMinimumDurations = (presetManagerState, equipment, previewDeviceUuid) => {
  const name = equipment
    .find(e => e.co_equip_uuid === previewDeviceUuid)
    ?.name?.toLowerCase()
    .replace(' ', '_');
  if (!name || !presetManagerState[name]) {
    return [];
  }
  const s = presetManagerState[name]?.minimum_durations?.eprop_val_uuid?.value || '[]';
  return JSON.parse(s);
};

export const getMinimumDurationFromTo = (minimumDurations, fromPresetUuid, toPresetUuid) => {
  if (!fromPresetUuid || !toPresetUuid) {
    return 10;
  }
  if (fromPresetUuid === toPresetUuid) {
    return 1;
  }
  return Math.ceil(
    minimumDurations.find(m => m.from === fromPresetUuid && m.to === toPresetUuid)?.duration || 10
  );
};
